import styled from 'styled-components';
import colors from 'styles/colors';

import { PrimaryButton } from 'components/Buttons';
import { INPUT_STYLES_MIXIN } from 'styles/form';

export const CLASSES = {
  DISABLED: 'disabled',
  FULL_WIDTH: 'full-width',
  FOCUSED: 'focused',
  HIGHLIGHTED: 'highlight',
  LIGHT: 'light',
  MONOCHROME: 'monochrome',
  NO_RIGHT_PADDING: 'no-right-padding',
  NO_BORDER: 'no-border',
  PRIMARY: 'primary',
  HAS_SELECTION: 'has-selection',
};

const DISABLED_STYLES = `
    background-color: ${colors.gray90};
    cursor: default;
    color: ${colors.inactive};

    &:hover {
        background-color: ${colors.gray90};
        color: ${colors.inactive};
    }
`;

const DISABLED_ITEM_STYLES = `
    cursor: default;
    color: ${colors.inactive};

    &:hover {
        background-color: ${colors.white};
        color: ${colors.inactive};
    }
`;

const SHARED_CONDITIONAL_STYLES = `
  .${CLASSES.DISABLED} & {
    ${DISABLED_STYLES};
  }

  .${CLASSES.NO_BORDER} & {
    border: 0;
  }
`;

export const Container = styled.div`
  display: inline-block;
  overflow: visible;

  &:hover {
    background-color: ${colors.white};
  }

  &.${CLASSES.FULL_WIDTH} {
    width: 100%;

    input,
    button {
      width: 100%;
      padding-left: 1em;
      padding-right: 1em;
    }
  }
`;

export const Icon = styled.i`
  font-size: 8px;
  margin-left: auto;
`;

export const DropdownContent = styled(PrimaryButton)`
  background-color: ${colors.white};
  border: 0;
  color: ${colors.primary};
  cursor: pointer;
  font-weight: 400;
  height: 30px;
  margin-top: 0;
  padding-bottom: 0;
  padding-top: 0;
  text-transform: none;

  & .button-children {
    width: inherit;
    & > div {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;

      .display-text {
        text-align: left;
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
      }
    }
  }

  &:not([disabled]) {
    &:hover {
      background-color: ${colors.white} !important;
    }
  }

  .${CLASSES.HIGHLIGHTED} & {
    color: ${colors.highlight};

    i {
      color: ${colors.highlight};
    }
  }

  .${CLASSES.NO_RIGHT_PADDING} & {
    padding-right: 0;
  }

  [class^='icon-'],
  [class*=' icon-'] {
    padding-left: 0.5em;
  }

  ${SHARED_CONDITIONAL_STYLES};
`;

export const DropdownInput = styled.input`
  ${INPUT_STYLES_MIXIN};
  ${SHARED_CONDITIONAL_STYLES};
`;

export const DropdownButton = styled(PrimaryButton)<{ $readOnly: boolean }>`
  color: ${colors.inactive};
  justify-content: space-between;
  font-weight: 600;

  & .button-children {
    width: inherit;

    & > div {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;

      .display-text {
        text-align: left;
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
      }
    }
  }

  &:not([disabled]) {
    &:hover,
    &:focus,
    .${CLASSES.HAS_SELECTION} &:hover,
    .${CLASSES.HAS_SELECTION} &:focus {
      color: ${colors.highlight};
    }

    .${CLASSES.HAS_SELECTION} & {
      color: ${colors.primary};
    }

    .${CLASSES.PRIMARY} & {
      font-weight: 700;
      font-size: 12px;
      color: ${colors.white};

      &:hover {
        color: ${colors.white};
      }
    }

    .${CLASSES.PRIMARY}.${CLASSES.HAS_SELECTION} & {
      color: ${colors.white};
    }
  }

  ${(props) =>
    props.$readOnly
      ? `
    color: ${colors.primary};
    cursor: default;
    border: none;
    border-color: #0000;
    background-color: #FFFFFF00;
    padding: 0;

    &:hover,
    &:focus {
      color: ${colors.primary};
      border: none;
      border-color: #0000;
      box-shadow: none;
      & > * {
        color: ${colors.primary};
      }
    }
  `
      : ''}

  & > svg {
    width: fit-content;
    overflow: visible;
    margin-right: 5px;
  }

  ${SHARED_CONDITIONAL_STYLES};
`;

export const SubtleDropdownButton = styled(DropdownButton)`
  ${INPUT_STYLES_MIXIN};
  text-transform: none;
  font-weight: 600;

  ${(props) =>
    props.$readOnly
      ? `
    color: ${colors.primary};
    cursor: default;
    border-color: #0000;
    padding: 0;
  `
      : ''}

  &:not([disabled]) {
    &:hover {
      background-color: ${colors.white} !important;
    }
  }
`;

export const DropdownMenuContent = styled.div`
  background-color: ${colors.white};
`;

export const DropdownMenuItem = styled.div<{ active?: boolean }>`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.primary};
  cursor: pointer;
  height: 30px;
  display: flex;
  align-items: center;
  padding: 0 20px;
  text-transform: none;

  &:hover,
  &.${CLASSES.FOCUSED} {
    background-color: ${colors.activeLight};
    color: ${colors.highlight};
  }

  ${(props) =>
    props.active &&
    `
      background-color: ${colors.activeLight};
        color: ${colors.highlight};
  `};

  & > svg {
    margin-right: 5px;
  }

  &.${CLASSES.DISABLED} {
    ${DISABLED_ITEM_STYLES};
  }
`;
